import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { authApi } from '../../api/endpoints'
import { useAuthStore } from '../../store/authStore'
import { Input, PasswordInput } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { BotaoGoogle } from '../../components/BotaoGoogle'
import type { LoginResponse } from '../../types'

const FEATURES = [
  { icon: 'ri-bank-line', text: 'Contas e cartões num só lugar' },
  { icon: 'ri-pie-chart-2-line', text: 'Orçamentos com alertas' },
  { icon: 'ri-links-line', text: 'Integração com Open Finance' },
]

/**
 * O cartão de login em si — sem a moldura de página (logo, tagline, rodapé), pra caber tanto
 * na página /login sozinha quanto embutido ao lado do texto de apresentação na home.
 */
export function LoginCard() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const sessaoExpirada = useAuthStore((s) => s.sessaoExpirada)

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [lembrar, setLembrar] = useState(false)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const { data: config } = useQuery({ queryKey: ['auth-config'], queryFn: authApi.configuracao, retry: false })

  function entrar(data: LoginResponse) {
    setAuth({
      userId: data.user.id,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      userName: data.user.name,
      userEmail: data.user.email,
      userRole: data.user.roles[0],
    })
    navigate('/dashboard')
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    setLoading(true)
    try {
      entrar(await authApi.login({ email, password: senha }))
    } catch {
      setErro('E-mail ou senha inválidos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  async function entrarComGoogle(idToken: string) {
    setErro(null)
    setLoading(true)
    try {
      entrar(await authApi.google(idToken))
    } catch (e) {
      setErro((e as Error).message || 'Não foi possível entrar com o Google.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-md rounded bg-surface p-6 shadow-dropdown sm:p-8">
      <div className="mb-6 text-center">
        <h5 className="text-xl font-semibold text-primary">Bem-vindo de volta!</h5>
        <p className="mt-1 text-13 text-muted">Entre para continuar no Walleon.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="E-mail"
          type="email"
          placeholder="seu@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />

        <PasswordInput
          label="Senha"
          placeholder="Digite sua senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          required
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={lembrar}
              onChange={(e) => setLembrar(e.target.checked)}
              className="h-4 w-4 rounded accent-primary"
            />
            <span className="text-13 text-muted">Lembrar meus dados</span>
          </label>
          <Link to="/esqueci-senha" className="text-13 text-primary hover:underline">Esqueci minha senha</Link>
        </div>

        {sessaoExpirada && !erro && (
          <div className="flex items-center gap-2 rounded bg-primary/10 px-3 py-2.5 text-13 text-primary" role="status">
            <i className="ri-time-line" aria-hidden /> Sua sessão expirou. Entre novamente para continuar.
          </div>
        )}

        {erro && (
          <div className="flex items-center gap-2 rounded bg-expense/10 px-3 py-2.5 text-13 text-expense" role="alert">
            <i className="ri-error-warning-line" aria-hidden /> {erro}
          </div>
        )}

        <Button type="submit" loading={loading} className="w-full">
          {loading ? 'Entrando...' : 'Entrar'}
        </Button>
      </form>

      {config?.googleHabilitado && config.googleClientId && (
        <>
          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <span className="text-2xs uppercase text-muted">ou</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <BotaoGoogle clientId={config.googleClientId} onCredential={entrarComGoogle} texto="signin_with" />
        </>
      )}

      <p className="mt-5 text-center text-13 text-muted">
        Ainda não tem conta? <Link to="/criar-conta" className="text-primary hover:underline">Criar conta grátis</Link>
      </p>

      <div className="mt-6 border-t border-border/60 pt-5">
        <ul className="grid gap-2 sm:grid-cols-3">
          {FEATURES.map((f) => (
            <li key={f.text} className="flex flex-col items-center gap-1 text-center">
              <i className={`${f.icon} text-lg text-primary`} aria-hidden />
              <span className="text-2xs text-muted">{f.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
